import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, inject, OnInit, OnChanges, SimpleChanges, AfterViewInit, signal } from '@angular/core';
import { FormGroup, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ViewNewRepApplication } from '../../new-rep-application-interfaces/viewnew-rep-application.interface';
import { MatButtonModule } from '@angular/material/button';
import { SessionStorageService } from '../../../../../core/services/session-storage/session-storage.service';
import { ContactDetailModel } from './contact-details-model/contact-detail-model';
import { ContactDetailService } from './contact-detail-service/contact-detail-service';
import { SnackbarService } from '../../../../../shared/services/snackbar/snackbar.service';
import { Router } from '@angular/router';
import { HttpStatusCode } from 'axios';
import { noWhitespace } from '../../../../../shared/validators/no-whitespace.validator';


@Component({
  selector: 'app-contact-details',
  imports: [MatFormField, MatIcon, CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule],
  templateUrl: './contact-details.component.html',
  styleUrl: './contact-details.component.scss'
})
export class ContactDetailsComponent implements OnInit {

  @Output() next = new EventEmitter<string[]>();
  @Output() save = new EventEmitter<string>();
  @Input() formGroup!: FormGroup;
  @Input() contactdetails!: ViewNewRepApplication;
  @Input() readonly = signal(false);
  status = 'Not Started';
  readonly message = 'This field is required.';
  data = history.state;

  private fb = inject(FormBuilder);
  private sessionStorageService = inject(SessionStorageService);
  private contactDetailService = inject(ContactDetailService);

  private snackbarService = inject(SnackbarService);
  private router = inject(Router);
  setSprinner = signal(false);
  private readonly MAX_PHONE_LENGTH = 13;

  role = '';
  isVisible = signal(false);

  async ngOnInit() {
    this.formGroup.addControl('house', this.fb.control('', [Validators.required, noWhitespace]));
    this.formGroup.addControl('street', this.fb.control('', [Validators.required, noWhitespace]));
    this.formGroup.addControl('level', this.fb.control('', [Validators.required, noWhitespace]));
    this.formGroup.addControl('building', this.fb.control(''));
    this.formGroup.addControl('postalCode', this.fb.control('', [Validators.required, noWhitespace]));

    this.formGroup.addControl('contactNumber', this.fb.control('', [Validators.pattern(/^\+?[0-9]{1,13}$/), Validators.maxLength(13)]));
    this.formGroup.addControl('mobileNumber', this.fb.control('', [Validators.required, Validators.pattern(/^\+?[0-9]{1,13}$/), Validators.maxLength(13), noWhitespace]));
    this.formGroup.addControl('personalEmailAddress', this.fb.control('', [Validators.required, Validators.email, noWhitespace]));

    this.role = this.sessionStorageService.getCurrentUserRole();
    // console.log(!this.contactdetails)
    if ((this.role === 'DSU' || this.role === 'DSU Manager') && !this.contactdetails) {
      this.isVisible.set(true);
      this.getContactDetails();
    } else {
      await this.fetchContactDetails();
    }
    this.handleFormStatus();
  }

  fetchContactDetails(): void {
    const address = this.contactdetails?.address.split(',');


    this.formGroup.controls['house']?.setValue(address[0]);
    this.formGroup.controls['street']?.setValue(address[1]);
    this.formGroup.controls['level']?.setValue(address[2]);
    this.formGroup.controls['building']?.setValue(address[3]);
    this.formGroup.controls['postalCode']?.setValue(address[4]);

    this.formGroup.controls['contactNumber']?.setValue(this.contactdetails?.homeNumber);
    this.formGroup.controls['mobileNumber']?.setValue(this.contactdetails?.mobileNumber);
    this.formGroup.controls['personalEmailAddress']?.setValue(this.contactdetails?.personalEmailAddress);
    if (this.formGroup.valid) {
      this.status = 'Completed';
      this.next.emit([this.status, 'submitted']);
    } else if (this.formGroup.invalid) {
      const anyFilled = Object.values(this.formGroup.value).some(v => v && v.toString().trim() !== '');
      if (anyFilled) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'submitted']);
      } else {
        this.status = 'Not Started';
        this.next.emit([this.status, 'submitted']);
      }
    }
    if (this.readonly() == true) {
      this.formGroup?.disable();

    } else {
      this.formGroup?.enable();
    }
  }

  sanitizeNumericInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const hasLeadingPlus = input.value.startsWith('+');
    const digits = input.value.replace(/\D/g, '');
    let sanitized = hasLeadingPlus ? '+' + digits : digits;

    if (sanitized.length > this.MAX_PHONE_LENGTH) {
      sanitized = sanitized.slice(0, this.MAX_PHONE_LENGTH);
    }

    if (sanitized !== input.value) {
      input.value = sanitized;
    }
    this.formGroup.get(controlName)?.setValue(sanitized);
  }

  allowNumbersOnly(event: KeyboardEvent): void {
        const charCode = event.which ? event.which : event.keyCode;
        const input = event.target as HTMLInputElement;

        if (charCode === 43) {                         // '+'
            if (input.selectionStart !== 0 || input.value.includes('+')) {
                event.preventDefault();                // only allowed once, only at position 0
            }
            return;
        }

        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
            return;
        }

        if (input.value.length >= this.MAX_PHONE_LENGTH && input.selectionStart === input.selectionEnd) {
            event.preventDefault();
        }
    }

  handleFormStatus() {
    if (this.readonly()) return;

    const initialValue = this.formGroup.getRawValue();

    this.formGroup.valueChanges.subscribe(currentValue => {
      const hasChanged =
        JSON.stringify(initialValue) !== JSON.stringify(currentValue);

      if (hasChanged) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'change']);
      }
    });
  }

  async saveAndContinue() {

    // Validation or API save logic here
    // Save API call or form validation here
    if (this.formGroup.valid) {
      this.status = 'Completed';

    }
    this.formGroup.markAllAsTouched();
    this.next.emit([this.status, 'submit']);
  }


  async saveSection() {
    this.setSprinner.set(true);
    let saveContactDetails = new ContactDetailModel();

    const address = `${this.formGroup.get('house')?.value},${this.formGroup.get('street')?.value},${this.formGroup.get('level')?.value},${this.formGroup.get('building')?.value},${this.formGroup.get('postalCode')?.value}`;
    saveContactDetails.setAddress(address);
    saveContactDetails.setPiasrepCode(this.data.applicant.repcode);
    saveContactDetails.setHomeNumber(this.formGroup.get('contactNumber')?.value);
    saveContactDetails.setMobileNumber(this.formGroup.get('mobileNumber')?.value);
    saveContactDetails.setPersonalEmailAddress(this.formGroup.get('personalEmailAddress')?.value);

    const responseData = await this.contactDetailService
      .saveContactDetails(saveContactDetails)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      // this.viewDsuAssessment();
      this.save.emit('saved');

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackbarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackbarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }

  }
  cancelSection() {
    this.save.emit('cancelled');
  }

  // GET CONTACT DETAILS
  async getContactDetails() {
    this.setSprinner.set(true);
    let repCode = this.data.applicant.repcode;

    const responseData = await this.contactDetailService
      .getContactDetails(repCode)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {

      this.contactdetails = responseData.data.responsedata.personal_particular;
      this.fetchContactDetails();

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackbarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackbarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }

  }

}
